import { join } from 'path';
import log from 'loglevel';
import axios, { AxiosRequestConfig } from 'axios';
import { CONFIG } from '../config';
import { API_ROUTES, ROUTES } from '../consts';
import { deleteUserTokens, setUserTokens, tokenExpirationDate } from '../utils';

type refreshTokenType = {
  requestConfig: AxiosRequestConfig,
  refreshToken: string,
}

const TAG = '[REFRESH_TOKEN]';

let refreshSubscribers: ((token: string) => void)[] = [];

let refreshing = false;

export const subscribeTokenRefresh = (cb: (token: string) => void): void => {
  refreshSubscribers.push(cb);
};

export const onRefreshed = (token: string): void => {
  refreshSubscribers = refreshSubscribers.filter((cb) => cb(token));
};

export const isRefreshing = (): boolean => refreshing;

export const setIsRefreshing = (flag: boolean): void => {
  refreshing = flag;
};

export const clearSubscribers = (): void => {
  refreshSubscribers = [];
};

export const refreshAndSetTokens = async (refreshToken: string): Promise<void> => {
  setIsRefreshing(true);
  log.debug(TAG, 'started refreshing token process');
  const path = join(CONFIG.PREFIX_URL, API_ROUTES.LOGIN || '');
  const url = CONFIG.BASE_URL ? join(CONFIG.BASE_URL, path || '') : path;
  const { data } = await axios.post(url, {
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
  });
  const {
    token_type: tokenType,
    access_token: accessToken,
    refresh_token: newRefreshToken,
    expires_in: expires,
  } = data;
  setUserTokens({
    tokenType,
    accessToken,
    refreshToken: newRefreshToken,
    expired: tokenExpirationDate(expires),
  });
  log.debug(TAG, 'access token was refreshed', data);
  onRefreshed(`${tokenType} ${accessToken}`);
  setIsRefreshing(false);
};

export const logoutAfterError = (): void => {
  log.warn(TAG, 'User was logged out, tokens deleted from local storage');
  deleteUserTokens();
  clearSubscribers();
  window.location.replace(ROUTES.LOGIN);
};

export const refresh = async ({
  requestConfig, refreshToken,
}: refreshTokenType): Promise<AxiosRequestConfig> => {
  const axiosConfigPr = new Promise((resolve) => {
    subscribeTokenRefresh((newToken) => {
      requestConfig.headers.Authorization = newToken;
      resolve(requestConfig);
    });
  });

  try {
    if (!isRefreshing()) {
      await refreshAndSetTokens(refreshToken);
    }
  } catch (err) {
    log.warn(TAG, 'failed to refresh token', err);
    logoutAfterError();

    return Promise.reject(err);
  }

  const refreshedConfig = await axiosConfigPr;

  return refreshedConfig as AxiosRequestConfig;
};
